import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import ArrowBack from '@mui/icons-material/ArrowBack';
import CheckCircleOutlineOutlined from '@mui/icons-material/CheckCircleOutlineOutlined';
import ErrorOutlineOutlined from '@mui/icons-material/ErrorOutlineOutlined';
import { api } from '../api';
import { splitPhones } from '../CounterpartyPanel';
import { PAY_METHODS } from '../PayDialog';
import ShareCodeDialog from '../ShareCodeDialog';
import { date, money, Tag, type Props, type StatusTone } from '../ui';
import { localStatementStep } from './Statements';

const toneColor: Record<StatusTone, string> = {
  success: '#1d7348',
  warning: '#9a5b06',
  info: '#24343a',
  neutral: '#5c6c72',
};
const toneBg: Record<StatusTone, string> = {
  success: '#eef8f2',
  warning: '#fff7ea',
  info: '#f4f6f7',
  neutral: '#f4f6f7',
};
const when = (v: string) => new Date(v).toLocaleString('zh-CN', { hour12: false }).slice(0, -3);

/**
 * A statement for a unit not on the platform. It took effect when issued, so what is left is the
 * money, and — as a record, not a step — the unit's own confirmation through a WeChat link.
 */
export function LocalStatementDetail(p: Props) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [s, setS] = useState<any>(null);
  const [sharing, setSharing] = useState(false);
  useEffect(() => {
    let live = true;
    setS(null);
    api(`/local-statements/${id}`)
      .then((d) => live && setS(d))
      .catch((e) => p.setError(e.message));
    return () => {
      live = false;
    };
  }, [id, p.version, p.workspace]);
  if (!s)
    return (
      <Box sx={{ p: 8, textAlign: 'center' }}>
        <CircularProgress />
      </Box>
    );

  const selling = s.kind === 'SALE';
  const verb = selling ? '收' : '付';
  const total = Number(s.total);
  const paid = Number(s.paid);
  const left = Math.max(0, total - paid);
  const issued = s.status === 'ISSUED';
  const step = localStatementStep(s);
  const post = (suffix: string, body: any = {}) => api(`/local-statements/${id}/${suffix}`, body);

  const record = () =>
    p.open({
      title: `登记${selling ? '收款' : '付款'}`,
      fields: [
        { name: 'amount', label: '金额（元）', decimals: 2 },
        {
          name: 'method',
          label: `${selling ? '收' : '付'}款方式`,
          options: Object.entries(PAY_METHODS).map(([value, label]) => ({ value, label })),
        },
        { name: 'reference', label: '凭据号 / 说明', wide: true },
      ],
      form: { amount: left.toFixed(2), method: 'BANK', reference: '' },
      submit: (f: any, key: string) => post('payments', { ...f, key }),
      description: `尚未结清 ${money(left)}。整笔登记，按订单先后分摊到各张订单。`,
    });
  const voidPayment = (x: any) =>
    p.open({
      title: '作废这笔记录',
      fields: [{ name: 'reason', label: '作废原因', wide: true }],
      form: { reason: '' },
      submit: (f: any) => post(`payments/${x.id}/void`, f),
      description: `${money(x.amount)} · ${PAY_METHODS[x.method]}。分摊到各订单的部分一并作废，记录保留。`,
    });
  const withdraw = () =>
    p.open({
      title: '撤回对账单',
      fields: [],
      form: {},
      submit: () => post('withdraw'),
      description: '撤回后这些订单回到待对账，可修改后重新生成；已发出的确认链接随之失效。',
    });
  const phones = () =>
    p.open({
      title: '对接手机号',
      fields: [{ name: 'phones', label: '对接手机号', wide: true }],
      form: { phones: '' },
      submit: (f: any) =>
        api(`/counterparties/${s.counterparty_id}/receiver-phones`, {
          phones: splitPhones(f.phones),
        }),
      description: `${s.counterparty} 签收送货单、确认对账单时，需用这些号码的微信验证。最多 3 个，空格分隔；保存会替换原有号码。`,
    });

  // ---- The one thing to do next ------------------------------------------------------------
  type Next = {
    tone: StatusTone;
    title: string;
    detail: string;
    primary?: [string, () => void];
    secondary?: [string, () => void];
  };
  const next: Next = !issued
    ? { tone: 'neutral', title: '对账单已撤回', detail: '这些订单已回到待对账。' }
    : s.link?.answer === 'DISPUTED' && left > 0
      ? {
          tone: 'warning',
          title: `${s.counterparty}有异议：${s.link.reason}`,
          detail:
            paid > 0
              ? '核对后作废已登记的款项再撤回重开，或按原金额继续。'
              : '核对后撤回重新生成，或按原金额继续。',
          primary: paid > 0 ? undefined : ['撤回重开', withdraw],
          secondary: [`登记${selling ? '收款' : '付款'}`, record],
        }
      : left <= 0
        ? {
            tone: 'success',
            title: '对账单已结清',
            detail: `共 ${money(total)}，已按订单分摊入账。`,
          }
        : {
            tone: 'warning',
            title: `待${verb}款 ${money(left)}`,
            detail: `对账单已生效，${verb}款后整笔登记，按订单先后分摊。`,
            primary: [`登记${selling ? '收款' : '付款'}`, record],
            // Money on its orders has to be voided before the statement can go back.
            secondary: paid > 0 ? undefined : ['撤回', withdraw],
          };
  const NextIcon =
    next.tone === 'success'
      ? CheckCircleOutlineOutlined
      : next.tone === 'warning'
        ? ErrorOutlineOutlined
        : null;

  // The unit's answer through the latest link.
  const link = s.link;
  const answer: [string, StatusTone] = !link
    ? ['未发送', 'neutral']
    : link.answer === 'CONFIRMED'
      ? ['对方已确认', 'success']
      : link.answer === 'DISPUTED'
        ? ['对方有异议', 'warning']
        : link.live
          ? ['等待对方确认', 'info']
          : ['链接已失效', 'neutral'];

  return (
    <>
      <Button
        component={Link}
        to="/statements"
        size="small"
        startIcon={<ArrowBack />}
        sx={{ mb: 2 }}
      >
        返回对账结算
      </Button>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <Box sx={{ flex: 1, minWidth: 260 }}>
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 1 }}>
              <Tag tone="info">{selling ? '应收对账单' : '应付对账单'}</Tag>
              <Tag tone="neutral">本企业登记</Tag>
              <Tag tone={step.tone}>{step.label}</Tag>
            </Stack>
            <Typography sx={{ fontSize: 24, fontWeight: 700, lineHeight: 1.3 }}>
              {s.counterparty}
            </Typography>
            <Typography color="text.secondary" sx={{ fontSize: 13, mt: 0.5 }}>
              {s.number} · {s.period.replace('-', ' 年 ')} 月 · {s.lines.length} 张订单 ·{' '}
              {s.created_name ? `${s.created_name} ` : ''}于 {date(s.created_at)} 发出
              {s.note && ` · ${s.note}`}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 5, alignItems: 'flex-end' }}>
            <Box>
              <Typography color="text.secondary" sx={{ fontSize: 12 }}>
                对账金额
              </Typography>
              <Typography sx={{ fontSize: 24, fontWeight: 700, lineHeight: 1.3 }}>
                {money(total)}
              </Typography>
            </Box>
            {issued && (
              <Box>
                <Typography color="text.secondary" sx={{ fontSize: 12 }}>
                  已{verb}
                </Typography>
                <Typography sx={{ fontSize: 24, fontWeight: 700, lineHeight: 1.3 }}>
                  {money(paid)}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>

        <Box
          sx={{
            mt: 2.5,
            px: 2,
            py: next.primary ? 1.5 : 1.25,
            borderRadius: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
            flexWrap: 'wrap',
            bgcolor: toneBg[next.tone],
          }}
        >
          {NextIcon && <NextIcon sx={{ color: toneColor[next.tone], fontSize: 18 }} />}
          <Box sx={{ flex: 1, minWidth: 220 }}>
            <Typography
              component="span"
              sx={{ fontSize: 14, fontWeight: 650, color: toneColor[next.tone] }}
            >
              {next.title}
            </Typography>
            <Typography component="span" color="text.secondary" sx={{ fontSize: 12.5, ml: 1.5 }}>
              {next.detail}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            {next.secondary && (
              <Button
                variant="outlined"
                color="inherit"
                onClick={next.secondary[1]}
                sx={{ borderColor: '#cfd9dc', color: 'text.primary', bgcolor: '#fff' }}
              >
                {next.secondary[0]}
              </Button>
            )}
            {next.primary && (
              <Button variant="contained" disableElevation onClick={next.primary[1]}>
                {next.primary[0]}
              </Button>
            )}
          </Stack>
        </Box>
      </Paper>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) 340px' },
          gap: 3,
          alignItems: 'start',
        }}
      >
        <Paper sx={{ minWidth: 0 }}>
          <div className="panel-title">
            <Typography sx={{ fontSize: 15, fontWeight: 650 }}>
              订单明细 · {s.lines.length} 张
            </Typography>
          </div>
          <Box sx={{ overflowX: 'auto' }}>
            <Table>
              <TableHead>
                <TableRow sx={{ '& th': { whiteSpace: 'nowrap' } }}>
                  <TableCell sx={{ minWidth: 200 }}>订单</TableCell>
                  <TableCell>完成{selling ? '出库' : '入库'}</TableCell>
                  <TableCell align="right">订单金额</TableCell>
                  <TableCell align="right">对账金额</TableCell>
                  {issued && <TableCell align="right">已{verb}</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {s.lines.map((l: any) => (
                  <TableRow
                    key={l.order_id}
                    hover
                    // The whole row opens the order; the name is the visible link.
                    onClick={() => navigate(`/local-orders/${l.order_id}`)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>
                      <Typography
                        component={Link}
                        to={`/local-orders/${l.order_id}`}
                        sx={{
                          fontSize: 14,
                          fontWeight: 600,
                          color: 'primary.main',
                          textDecoration: 'none',
                          'tr:hover &': { textDecoration: 'underline' },
                        }}
                      >
                        {l.sku_name}
                        {l.line_count > 1 && ` 等 ${l.line_count} 种`}
                      </Typography>
                      <Typography color="text.secondary" sx={{ fontSize: 12 }}>
                        {l.number}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{date(l.delivered_at)}</TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap', color: 'text.secondary' }}>
                      {money(l.order_amount)}
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}>
                      {money(l.amount)}
                    </TableCell>
                    {issued && (
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        {money(l.paid)}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
              <TableFooter>
                <TableRow>
                  <TableCell colSpan={3} sx={{ fontSize: 13, color: 'text.secondary' }}>
                    合计
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      fontSize: 15,
                      fontWeight: 700,
                      color: 'text.primary',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {money(total)}
                  </TableCell>
                  {issued && (
                    <TableCell
                      align="right"
                      sx={{ fontSize: 13, color: 'text.primary', whiteSpace: 'nowrap' }}
                    >
                      {money(paid)}
                    </TableCell>
                  )}
                </TableRow>
              </TableFooter>
            </Table>
          </Box>
        </Paper>

        <Stack spacing={3}>
          <Paper sx={{ p: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ fontSize: 14, fontWeight: 650, flex: 1 }}>对方确认</Typography>
              <Tag tone={answer[1]}>{answer[0]}</Tag>
            </Box>
            {link?.answer ? (
              <Typography sx={{ fontSize: 13, mt: 1, lineHeight: 1.7 }}>
                {link.signer || s.counterparty}
                {link.phone && `（${link.phone.slice(0, 3)}****${link.phone.slice(7)}）`}于{' '}
                {when(link.answeredAt)} {link.answer === 'CONFIRMED' ? '确认无误' : '提出异议'}
                {link.answer === 'DISPUTED' && (
                  <Box component="span" sx={{ display: 'block', color: 'warning.dark' }}>
                    {link.reason}
                  </Box>
                )}
              </Typography>
            ) : (
              <Typography color="text.secondary" sx={{ fontSize: 12.5, mt: 1, lineHeight: 1.7 }}>
                {link?.live
                  ? `${when(link.sentAt)} 发出，对方回复后会出现在这里。`
                  : '不影响收付款。发给对方核对，确认或异议都会留在这里作为凭证。'}
              </Typography>
            )}
            {/* Once confirmed there is nothing more to ask the unit. */}
            {issued && link?.answer !== 'CONFIRMED' && (
              <>
                <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography
                    sx={{
                      fontSize: 12.5,
                      flex: 1,
                      color: s.phones.length ? 'text.secondary' : 'warning.dark',
                    }}
                  >
                    {s.phones.length
                      ? `对接手机号 ${s.phones.join('、')}`
                      : '未登记对接手机号，对方扫码后无法确认'}
                  </Typography>
                  <Button size="small" onClick={phones}>
                    {s.phones.length ? '修改' : '登记'}
                  </Button>
                </Box>
                <Button
                  fullWidth
                  variant="outlined"
                  sx={{ mt: 1.5 }}
                  onClick={() => setSharing(true)}
                >
                  {link ? '重新发给对方' : '发给对方确认'}
                </Button>
              </>
            )}
          </Paper>

          <Paper sx={{ p: 2.5 }}>
            <Typography sx={{ fontSize: 14, fontWeight: 650 }}>收付款</Typography>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1.5, mb: 0.75 }}>
              <Typography sx={{ fontSize: 12.5 }}>
                已{verb} {money(paid)}
              </Typography>
              <Typography color="text.secondary" sx={{ fontSize: 12.5 }}>
                余 {money(left)}
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={Math.min(100, (paid / Math.max(total, 0.01)) * 100)}
              color="success"
              sx={{ height: 6, borderRadius: 3, bgcolor: '#edf1f2' }}
            />
            {s.payments.length > 0 && (
              <Stack spacing={0.75} sx={{ mt: 1.5 }}>
                {s.payments.map((x: any) => (
                  <Box
                    key={x.id}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      fontSize: 13,
                      color: x.voided_at ? 'text.disabled' : 'text.primary',
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box
                        component="span"
                        sx={{
                          fontWeight: 650,
                          textDecoration: x.voided_at ? 'line-through' : 'none',
                        }}
                      >
                        {money(x.amount)}
                      </Box>{' '}
                      · {PAY_METHODS[x.method]}
                      {x.reference && ` · ${x.reference}`}
                      <Box component="span" sx={{ color: 'text.secondary', ml: 1, fontSize: 12 }}>
                        {date(x.created_at)}
                        {x.voided_at && ` · 已作废：${x.void_reason}`}
                      </Box>
                    </Box>
                    {!x.voided_at && issued && (
                      <Button size="small" color="inherit" onClick={() => voidPayment(x)}>
                        作废
                      </Button>
                    )}
                  </Box>
                ))}
              </Stack>
            )}
            <Typography color="text.secondary" sx={{ fontSize: 11.5, mt: 1.5 }}>
              逐单登记的{verb}款也计入这里。
            </Typography>
          </Paper>
        </Stack>
      </Box>

      {sharing && (
        <ShareCodeDialog
          number={s.number}
          title="发给对方确认"
          hint="把小程序码发给对方，对方微信扫码后核对并确认；之前发出未回复的链接会失效"
          load={() =>
            api<{ image: string }>(`/local-statements/${s.id}/sign-code`, {}).then((r) => r.image)
          }
          onClose={() => {
            setSharing(false);
            p.reload();
          }}
          notify={p.notify}
        />
      )}
    </>
  );
}
